import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const RANK_BORDER = { normal: '#93c5fd', silver: '#cbd5e1', gold: '#fbbf24', platinum: '#1f2937', blacklist: '#f87171', new: '#4ade80' };
function getBorderColor(c) {
  if (c.is_blacklisted) return RANK_BORDER.blacklist;
  if (c.ranking && c.ranking !== 'normal') return RANK_BORDER[c.ranking] || RANK_BORDER.normal;
  if (c.is_new) return RANK_BORDER.new;
  return RANK_BORDER.normal;
}
function copyName(name) {
  navigator.clipboard.writeText(name || '').then(() => toast.success('Copied: ' + name)).catch(() => {});
}
function Avatar({ c, size = 36 }) {
  const border = getBorderColor(c);
  const st = { width: size, height: size, borderRadius: '50%', border: `2.5px solid ${border}`, flexShrink: 0, objectFit: 'cover', cursor: 'pointer' };
  const el = c.fb_profile_pic
    ? <img src={c.fb_profile_pic} alt="" style={st} />
    : <div style={{ ...st, background: 'linear-gradient(135deg,#7c6af5,#c084fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 700, color: '#fff' }}>{(c.customer_name || '?')[0].toUpperCase()}</div>;
  return <div onClick={e => { e.stopPropagation(); copyName(c.customer_name); }} title="Click to copy name" style={{ flexShrink: 0 }}>{el}</div>;
}

const TABS = [
  { key: 'VET', label: '🚚 VET' },
  { key: 'JT_COD', label: '📦 J&T COD' },
  { key: 'JT_NON_COD', label: '📦 J&T Non COD' },
  { key: 'DOM', label: '🛵 DOM' },
  { key: 'SONA', label: '🛵 Sona' },
  { key: 'TAKE_SHOP', label: '🏪 Take Shop' },
  { key: 'GRAB', label: '🟢 Grab' },
  { key: 'NO_DELIVERY', label: '❓ No Delivery' },
  { key: 'POSTPONE', label: '⏳ Postpone' },
];
const TAB_COLORS = { VET:'#2563eb', JT_COD:'#7c3aed', JT_NON_COD:'#9333ea', DOM:'#0891b2', SONA:'#0891b2', TAKE_SHOP:'#16a34a', GRAB:'#15803d', NO_DELIVERY:'#9ca3af', POSTPONE:'#f59e0b' };

function cleanPhoneQF(raw) {
  if (!raw) return '';
  const khmer = '០១២៣៤៥៦៧៨៩';
  let s = raw.split('').map(c => { const i = khmer.indexOf(c); return i >= 0 ? String(i) : c; }).join('');
  return s.replace(/[^\d+]/g, '');
}

// ── Quick Edit Popup ──────────────────────────────────────────────────────────
function QuickEditPopup({ customer, deliveryMethods, onClose, onSaved }) {
  const [editingPhone, setEditingPhone] = useState(false);
  const [phones, setPhones] = useState([]);
  const [editingAddr, setEditingAddr] = useState(false);
  const [address, setAddress] = useState(customer.customer_address || '');
  const [delivery, setDelivery] = useState(customer.preferred_delivery || '');
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const inpSt = { background: '#fff', border: '1px solid #7c6af5', borderRadius: 6, padding: '4px 8px', fontSize: 12, outline: 'none', width: '100%' };
  const ab = (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' });
  const lbl = { fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 4, display: 'block' };
  const row = { marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f3f4f6' };

  const startPhone = () => {
    const p = customer.customer_phones?.length ? [...customer.customer_phones] : customer.customer_phone ? [customer.customer_phone] : [''];
    setPhones(p); setEditingPhone(true);
  };
  const savePhone = async () => {
    const cleaned = phones.map(cleanPhoneQF).filter(Boolean);
    try {
      await api.patch(`/customers/${customer.customer_id}`, { phones: cleaned, phone: cleaned[0]||'', phone2: cleaned[1]||'' });
      toast.success('Phone saved'); setEditingPhone(false); onSaved();
    } catch { toast.error('Failed'); }
  };
  const saveAddr = async () => {
    try {
      await api.patch(`/customers/${customer.customer_id}`, { address });
      toast.success('Address saved'); setEditingAddr(false); onSaved();
    } catch { toast.error('Failed'); }
  };
  const saveDel = async val => {
    try {
      await api.patch(`/customers/${customer.customer_id}`, { preferred_delivery: val || null });
      toast.success(val ? 'Delivery set' : 'Cleared'); setDelivery(val); onSaved();
    } catch { toast.error('Failed'); }
  };

  return (
    <div ref={ref} style={{ position: 'absolute', top: '100%', left: 0, zIndex: 500, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: 16, minWidth: 280, marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{customer.customer_name}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>✕</button>
      </div>

      <div style={row}>
        <span style={lbl}>📞 PHONE</span>
        {editingPhone ? (
          <div>
            {phones.map((p, i) => (
              <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <input value={p} onChange={e => { const n=[...phones]; n[i]=e.target.value; setPhones(n); }}
                  onKeyDown={e => { if(e.key==='Enter') savePhone(); if(e.key==='Escape') setEditingPhone(false); }}
                  style={inpSt} autoFocus={i===0} placeholder={i===0?'Primary':'Phone '+(i+1)} />
                {phones.length > 1 && <button onClick={() => setPhones(phones.filter((_,j)=>j!==i))} style={ab('#fee2e2','#dc2626')}>×</button>}
              </div>
            ))}
            <div style={{ display:'flex', gap:4, marginTop:4 }}>
              <button onClick={() => setPhones([...phones,''])} style={ab('#f0f9ff','#0369a1')}>+</button>
              <button onClick={savePhone} style={ab('#7c6af5','#fff')}>✓</button>
              <button onClick={() => setEditingPhone(false)} style={ab('#f3f4f6','#374151')}>✕</button>
            </div>
          </div>
        ) : (
          <div onClick={startPhone} style={{ cursor:'text', minHeight:20 }} title="Click to edit">
            {(customer.customer_phones?.length ? customer.customer_phones : customer.customer_phone ? [customer.customer_phone] : []).map((p,i)=>(
              <div key={i} style={{ fontSize:12, color:'#374151' }}>{p}</div>
            ))}
            {!customer.customer_phone && !customer.customer_phones?.length && <span style={{ color:'#adb5bd', fontSize:12 }}>+ Add phone</span>}
          </div>
        )}
      </div>

      <div style={row}>
        <span style={lbl}>📍 ADDRESS</span>
        {editingAddr ? (
          <div>
            <input value={address} onChange={e => setAddress(e.target.value)} autoFocus
              onKeyDown={e => { if(e.key==='Enter') saveAddr(); if(e.key==='Escape') setEditingAddr(false); }}
              style={{ ...inpSt, marginBottom:4 }} placeholder="Address..." />
            <div style={{ display:'flex', gap:4 }}>
              <button onClick={saveAddr} style={ab('#7c6af5','#fff')}>✓ Save</button>
              <button onClick={() => setEditingAddr(false)} style={ab('#f3f4f6','#374151')}>✕</button>
            </div>
          </div>
        ) : (
          <div onClick={() => { setAddress(customer.customer_address||''); setEditingAddr(true); }} style={{ cursor:'text', minHeight:20 }} title="Click to edit">
            {customer.customer_address
              ? <div style={{ fontSize:12, color:'#374151' }}>{customer.customer_address}</div>
              : <span style={{ color:'#adb5bd', fontSize:12 }}>+ Add address</span>}
          </div>
        )}
      </div>

      <div>
        <span style={lbl}>🚚 PREFERRED DELIVERY</span>
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          {deliveryMethods.map(m => (
            <button key={m.code} onClick={() => saveDel(delivery===m.code?'':m.code)}
              style={{ background: delivery===m.code?'#0369a1':'#e0f2fe', color: delivery===m.code?'#fff':'#0369a1', border:'none', borderRadius:6, padding:'3px 9px', fontSize:11, fontWeight:600, cursor:'pointer' }}>
              {m.name}
            </button>
          ))}
          {delivery && <button onClick={() => saveDel('')} style={ab('#fee2e2','#dc2626')}>clear</button>}
        </div>
      </div>
    </div>
  );
}

// ── Postpone Modal ────────────────────────────────────────────────────────────
function PostponeModal({ customer, onClose, onSaved }) {
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'7px 10px', fontSize:13, width:'100%', boxSizing:'border-box', outline:'none' };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/billing/postpone/${customer.customer_id}`, { postpone_date: date||null, postpone_note: note||null });
      toast.success('Marked as postpone');
      onSaved();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:600, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:340, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ fontWeight:700, fontSize:15, marginBottom:16, color:'#111827' }}>⏳ Postpone — {customer.customer_name}</div>
        <div style={{ marginBottom:12 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Follow-up Date</label>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={inp} />
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:4 }}>Note (optional)</label>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Call back after 15 June" style={inp} />
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, background:'#f3f4f6', color:'#374151', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ flex:1, background:'#f59e0b', color:'#fff', border:'none', borderRadius:8, padding:'9px 0', fontSize:13, fontWeight:600, cursor:'pointer' }}>{saving?'Saving...':'Postpone'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function BillingQueue() {
  const [tab, setTab] = useState('VET');
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [billForms, setBillForms] = useState({});
  const [deselectedOrders, setDeselectedOrders] = useState({}); // cid -> Set of order ids
  const [creating, setCreating] = useState({});
  const [selected, setSelected] = useState({});
  const [bulkCreating, setBulkCreating] = useState(false);
  const [freeThreshold, setFreeThreshold] = useState(50);
  const [deliveryMethods, setDeliveryMethods] = useState([]);
  const [settings, setSettings] = useState({});
  const [dmLog, setDmLog] = useState({}); // customer_id -> {sent_by, sent_at}
  const [quickEdit, setQuickEdit] = useState(null);
  const [postponeModal, setPostponeModal] = useState(null);
  // filters
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    loadQueue();
    api.get('/settings/public').then(r => {
      setFreeThreshold(parseFloat(r.data.free_delivery_threshold||50));
      setSettings(r.data);
    }).catch(()=>{});
    api.get('/customers/delivery-methods').then(r => setDeliveryMethods(r.data.filter(m=>m.is_active))).catch(()=>{});
  }, []);

  async function loadQueue() {
    setLoading(true);
    try {
      const r = await api.get('/billing/queue');
      setQueue(r.data.queue || []);
    } catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }

  function getTab(c) {
    if (c.is_postpone) return 'POSTPONE';
    if (!c.preferred_delivery) return 'NO_DELIVERY';
    return c.preferred_delivery;
  }

  function initForm(customer) {
    const fee = customer.total >= freeThreshold ? 0 : 2;
    return { delivery_fee: fee, discount: 0, staff_note: '' };
  }

  // Apply filters
  const applyFilters = (list) => {
    return list.filter(c => {
      if (search) {
        const q = search.toLowerCase();
        const phones = (c.customer_phones||[]).join(' ') + ' ' + (c.customer_phone||'');
        if (!c.customer_name?.toLowerCase().includes(q) && !phones.includes(q)) return false;
      }
      if (dateFrom || dateTo) {
        const d = c.latest_order_date ? c.latest_order_date.slice(0,10) : null;
        if (!d) return false;
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
      }
      return true;
    });
  };

  const tabCounts = {};
  TABS.forEach(t => { tabCounts[t.key] = queue.filter(c => getTab(c) === t.key).length; });

  let filtered = queue.filter(c => getTab(c) === tab);
  // Sort postpone tab by date ascending (nearest first)
  if (tab === 'POSTPONE') {
    filtered = [...filtered].sort((a, b) => {
      if (!a.postpone_date) return 1;
      if (!b.postpone_date) return -1;
      return a.postpone_date.localeCompare(b.postpone_date);
    });
  }
  filtered = applyFilters(filtered);

  const selectedList = Object.values(selected);

  function toggleExpand(cid, customer) {
    setExpanded(p => ({ ...p, [cid]: !p[cid] }));
    if (!billForms[cid]) setBillForms(p => ({ ...p, [cid]: initForm(customer) }));
  }
  function updateForm(cid, field, value) {
    setBillForms(p => ({ ...p, [cid]: { ...p[cid], [field]: value } }));
  }
  function toggleSelect(cid, customer) {
    setSelected(p => {
      const n = {...p};
      if (n[cid]) { delete n[cid]; }
      else { n[cid] = customer; if (!billForms[cid]) setBillForms(f => ({...f, [cid]: initForm(customer)})); }
      return n;
    });
  }
  function selectAll() {
    const n = {};
    filtered.forEach(c => { n[c.customer_id] = c; if (!billForms[c.customer_id]) setBillForms(f=>({...f,[c.customer_id]:initForm(c)})); });
    setSelected(n);
  }

  async function createBill(customer) {
    const form = billForms[customer.customer_id] || initForm(customer);
    const selectedOrders = getSelectedOrders(customer);
    if (selectedOrders.length === 0) { toast.error('Select at least one order'); return; }
    setCreating(p => ({ ...p, [customer.customer_id]: true }));
    try {
      const r = await api.post('/billing/create', {
        customer_id: customer.customer_id,
        order_ids: selectedOrders.map(o => o.id),
        delivery_category: customer.preferred_delivery || 'no_contact',
        delivery_company: 'own', delivery_type: 'cod',
        delivery_fee: parseFloat(form.delivery_fee||0),
        discount: parseFloat(form.discount||0),
        staff_note: form.staff_note||'',
      });
      toast.success(`Bill ${r.data.bill_number} created!`);
      setSelected(p => { const n={...p}; delete n[customer.customer_id]; return n; });
      loadQueue();
    } catch(e) { toast.error(e.response?.data?.detail||'Failed'); }
    finally { setCreating(p => ({ ...p, [customer.customer_id]: false })); }
  }

  async function bulkCreate() {
    setBulkCreating(true);
    let ok=0, fail=0;
    for (const c of selectedList) {
      const form = billForms[c.customer_id] || initForm(c);
      try {
        const selOrders = getSelectedOrders(c);
      if (selOrders.length === 0) { fail++; continue; }
      await api.post('/billing/create', {
          customer_id: c.customer_id, order_ids: selOrders.map(o=>o.id),
          delivery_category: c.preferred_delivery||'no_contact',
          delivery_company:'own', delivery_type:'cod',
          delivery_fee: parseFloat(form.delivery_fee||0),
          discount: parseFloat(form.discount||0),
          staff_note: form.staff_note||'',
        });
        ok++;
      } catch { fail++; }
    }
    toast.success(`${ok} bill${ok>1?'s':''} created${fail>0?`, ${fail} failed`:''}`);
    setSelected({}); setBulkCreating(false); loadQueue();
  }

  async function removePostpone(customer) {
    try {
      await api.post(`/billing/postpone/${customer.customer_id}`);
      toast.success('Postpone removed'); loadQueue();
    } catch { toast.error('Failed'); }
  }

  const PROVINCE_TABS = ['VET', 'JT_NON_COD'];

  function buildDmMessage(customer, form) {
    const subtotal = customer.total;
    const fee = parseFloat(form?.delivery_fee || 0);
    const discount = parseFloat(form?.discount || 0);
    const total = subtotal + fee - discount;
    const orderLines = customer.orders.map(o => {
      const prod = o.product_code ? `${o.product_code} x${o.quantity}` : `x${o.quantity}`;
      return `• ${o.order_number} — ${prod} — $${o.total.toFixed(2)}`;
    }).join('\n');

    let template = settings.dm_order_summary ||
      'សួស្តី {name} 👋\n\nសង្ខេបការបញ្ជាទិញរបស់អ្នក៖\n{order_lines}\nសរុបរង៖ ${subtotal}\nដឹកជញ្ជូន៖ ${delivery_fee}\nសរុបទាំងអស់៖ ${total}\n\nសូមអរគុណសម្រាប់ការគាំទ្រ 🙏\n123 Crystal';

    let msg = template
      .replace('{name}', customer.customer_name || '')
      .replace('{order_lines}', orderLines)
      .replace('{subtotal}', subtotal.toFixed(2))
      .replace('{delivery_fee}', fee.toFixed(2))
      .replace('{total}', total.toFixed(2));

    // Append payment info for province customers
    if (PROVINCE_TABS.includes(customer.preferred_delivery)) {
      const payInfo = (settings.dm_payment_info || 'ABA: {aba_account_name}\nលេខគណនី: {aba_account_number}')
        .replace('{aba_account_name}', settings.aba_account_name || '')
        .replace('{aba_account_number}', settings.aba_account_number || '');
      msg = msg + '\n\n' + payInfo;
    }
    return msg;
  }

  async function copyDm(customer) {
    const form = billForms[customer.customer_id] || initForm(customer);
    const msg = buildDmMessage(customer, form);
    try {
      await navigator.clipboard.writeText(msg);
      toast.success('📋 DM copied! Paste into Messenger');
      // Log to backend
      const r = await api.post(`/billing/send-dm/${customer.customer_id}`, { dm_type: 'order_summary', message: msg });
      setDmLog(p => ({ ...p, [customer.customer_id]: { sent_by: r.data.sent_by, sent_at: r.data.sent_at } }));
    } catch { toast.error('Failed to copy'); }
  }

  function toggleOrder(cid, orderId) {
    setDeselectedOrders(p => {
      const set = new Set(p[cid] || []);
      if (set.has(orderId)) set.delete(orderId);
      else set.add(orderId);
      return { ...p, [cid]: set };
    });
  }

  function getSelectedOrders(customer) {
    const desel = deselectedOrders[customer.customer_id] || new Set();
    return customer.orders.filter(o => !desel.has(o.id));
  }

  const s = {
    tab: (active, color) => ({ padding:'8px 14px', border:'none', background:'none', cursor:'pointer', fontSize:12, fontWeight:active?700:400, color:active?color:'#6b7280', borderBottom:active?`3px solid ${color}`:'3px solid transparent', marginBottom:-2, whiteSpace:'nowrap' }),
    badge: color => ({ display:'inline-block', padding:'1px 7px', borderRadius:10, fontSize:11, fontWeight:600, background:color+'20', color, marginLeft:5 }),
    th: { padding:'6px 10px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12, background:'#f9fafb' },
    td: { padding:'7px 10px', fontSize:13, color:'#111827' },
    inp: { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 10px', fontSize:13, boxSizing:'border-box', outline:'none' },
  };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>🧾 Billing Queue</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>{queue.length} customers · Free delivery ≥ ${freeThreshold}</p>
        </div>
        {selectedList.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:13, color:'#6b7280' }}>{selectedList.length} selected</span>
            <button onClick={() => setSelected({})} style={{ background:'#f3f4f6', color:'#374151', border:'none', borderRadius:6, padding:'7px 14px', fontSize:13, cursor:'pointer' }}>Clear</button>
            <button onClick={bulkCreate} disabled={bulkCreating}
              style={{ background:'linear-gradient(135deg,#7c6af5,#c084fc)', color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              {bulkCreating?'Creating...':`✅ Create ${selectedList.length} Bill${selectedList.length>1?'s':''}`}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', padding:'12px 14px', marginBottom:12, display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:'1 1 180px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name or phone..."
            style={{ ...s.inp, width:'100%', paddingLeft:28 }} />
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>LAST ORDER FROM</div>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{ ...s.inp, width:'100%' }} />
        </div>
        <div style={{ flex:'0 0 130px' }}>
          <div style={{ fontSize:11, fontWeight:600, color:'#6b7280', marginBottom:3 }}>TO</div>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{ ...s.inp, width:'100%' }} />
        </div>
        {(search||dateFrom||dateTo) && (
          <button onClick={()=>{setSearch('');setDateFrom('');setDateTo('');}} style={{ background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:6, padding:'6px 12px', fontSize:12, cursor:'pointer' }}>✕ Clear</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ background:'#fff', borderRadius:8, border:'1px solid #e5e7eb', marginBottom:14, overflowX:'auto' }}>
        <div style={{ display:'flex', borderBottom:'2px solid #e5e7eb', paddingLeft:8 }}>
          {TABS.map(t => {
            const color = TAB_COLORS[t.key]||'#6b7280';
            return (
              <button key={t.key} style={s.tab(tab===t.key, color)} onClick={()=>{setTab(t.key);setSelected({});}}>
                {t.label}{tabCounts[t.key]>0&&<span style={s.badge(color)}>{tabCounts[t.key]}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Select all */}
      {filtered.length > 0 && (
        <div style={{ display:'flex', gap:10, marginBottom:10, fontSize:13, color:'#6b7280' }}>
          <button onClick={selectAll} style={{ background:'none', border:'none', color:'#7c6af5', fontWeight:600, cursor:'pointer', fontSize:13, padding:0 }}>Select All ({filtered.length})</button>
          {selectedList.length>0&&<button onClick={()=>setSelected({})} style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:13, padding:0 }}>Clear</button>}
        </div>
      )}

      {loading && <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>Loading...</div>}
      {!loading && filtered.length===0 && <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>🎉 No customers in this queue</div>}

      {filtered.map(customer => {
        const cid = customer.customer_id;
        const isOpen = expanded[cid];
        const isSelected = !!selected[cid];
        const form = billForms[cid] || initForm(customer);
        const desel = deselectedOrders[cid] || new Set();
        const selectedOrds = customer.orders.filter(o => !desel.has(o.id));
        const subtotal = selectedOrds.reduce((s,o) => s + o.total, 0);
        const autoFee = subtotal >= freeThreshold ? 0 : 2;
        const currentFee = parseFloat(form.delivery_fee);
        // Auto-update fee in form if subtotal crosses threshold
        if (!isNaN(currentFee) && currentFee !== autoFee && (currentFee === 0 || currentFee === 2)) {
          setTimeout(() => updateForm(cid, 'delivery_fee', autoFee), 0);
        }
        const grandTotal = subtotal + parseFloat(form.delivery_fee||0) - parseFloat(form.discount||0);
        const phones = customer.customer_phones?.length ? customer.customer_phones : customer.customer_phone ? [customer.customer_phone] : [];
        const freeDelivery = subtotal >= freeThreshold;
        const isPostponeTab = tab === 'POSTPONE';

        return (
          <div key={cid} style={{ background:'#fff', border:`1px solid ${isSelected?'#7c6af5':'#e5e7eb'}`, borderRadius:10, marginBottom:10, overflow:'visible', position:'relative' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px' }}>
              <input type="checkbox" checked={isSelected} onChange={()=>toggleSelect(cid,customer)}
                onClick={e=>e.stopPropagation()}
                style={{ width:16, height:16, cursor:'pointer', accentColor:'#7c6af5', flexShrink:0 }} />
              <Avatar c={customer} size={36} />

              {/* Info */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                  <div style={{ position:'relative' }}>
                    <span onClick={e=>{e.stopPropagation();setQuickEdit(quickEdit===cid?null:cid);}} style={{ fontSize:14, fontWeight:700, color:'#111827', cursor:'pointer' }}>{customer.customer_name}</span>
                    {quickEdit===cid&&(
                      <QuickEditPopup customer={customer} deliveryMethods={deliveryMethods}
                        onClose={()=>setQuickEdit(null)}
                        onSaved={()=>{setQuickEdit(null);loadQueue();}} />
                    )}
                  </div>
                  {customer.is_blacklisted?<span style={{fontSize:10}}>⛔</span>
                    :customer.ranking&&customer.ranking!=='normal'?<span style={{fontSize:10}}>{customer.ranking==='platinum'?'💎':customer.ranking==='gold'?'🥇':'🥈'}</span>
                    :customer.is_new?<span style={{fontSize:10}}>🟢</span>:null}
                  {customer.days_pending>3&&<span style={{fontSize:11,background:'#fee2e2',color:'#dc2626',padding:'1px 6px',borderRadius:8,fontWeight:600}}>⚠️ {customer.days_pending}d</span>}
                  {freeDelivery&&<span style={{fontSize:11,background:'#dcfce7',color:'#16a34a',padding:'1px 6px',borderRadius:8,fontWeight:600}}>🆓 Free</span>}
                  {isPostponeTab&&customer.postpone_date&&<span style={{fontSize:11,background:'#fef3c7',color:'#f59e0b',padding:'1px 6px',borderRadius:8,fontWeight:600}}>📅 {customer.postpone_date}</span>}
                </div>
                <div onClick={()=>toggleExpand(cid,customer)} style={{ display:'flex', gap:10, marginTop:3, flexWrap:'wrap', cursor:'pointer' }}>
                  {phones.length>0&&<span style={{fontSize:12,color:'#6b7280'}}>📞 {phones.join(' / ')}</span>}
                  {customer.customer_address&&<span style={{fontSize:12,color:'#6b7280'}}>📍 {customer.customer_address}</span>}
                  <span style={{fontSize:12,color:'#9ca3af'}}>{customer.order_count} order{customer.order_count>1?'s':''} · Last: {customer.latest_order_date}</span>
                  {isPostponeTab&&customer.postpone_note&&<span style={{fontSize:12,color:'#f59e0b'}}>📝 {customer.postpone_note}</span>}
                </div>
              </div>

              {/* Right */}
              <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:16, fontWeight:700, color:'#7c6af5' }}>${subtotal.toFixed(2)}</div>
                  <div style={{ fontSize:11, color:'#9ca3af' }}>subtotal</div>
                </div>

                {/* Copy DM button */}
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                  <button onClick={e=>{e.stopPropagation();copyDm(customer);}}
                    title={PROVINCE_TABS.includes(customer.preferred_delivery) ? 'Copy order summary + payment info' : 'Copy order summary'}
                    style={{ background: dmLog[customer.customer_id]?'#dcfce7':'#eff6ff', color: dmLog[customer.customer_id]?'#16a34a':'#2563eb', border:'none', borderRadius:6, padding:'4px 8px', fontSize:12, cursor:'pointer', whiteSpace:'nowrap' }}>
                    {PROVINCE_TABS.includes(customer.preferred_delivery) ? '📨 Copy+Pay' : '📋 Copy DM'}
                  </button>
                  {dmLog[customer.customer_id] && <div style={{ fontSize:9, color:'#9ca3af', textAlign:'center' }}>{dmLog[customer.customer_id].sent_at}</div>}
                </div>
                {/* Postpone button */}
                {isPostponeTab
                  ? <button onClick={e=>{e.stopPropagation();removePostpone(customer);}}
                      style={{ background:'#dcfce7', color:'#16a34a', border:'none', borderRadius:6, padding:'4px 8px', fontSize:11, fontWeight:600, cursor:'pointer' }}>✓ Done</button>
                  : <button onClick={e=>{e.stopPropagation();setPostponeModal(customer);}}
                      title="Mark as postpone"
                      style={{ background:'#f3f4f6', color:'#6b7280', border:'none', borderRadius:6, padding:'4px 8px', fontSize:12, cursor:'pointer' }}>⏳</button>
                }
                <div onClick={()=>toggleExpand(cid,customer)} style={{ cursor:'pointer' }}>
                  {isOpen?<ChevronUp size={16} color="#9ca3af"/>:<ChevronDown size={16} color="#9ca3af"/>}
                </div>
              </div>
            </div>

            {/* Expanded */}
            {isOpen&&(
              <div style={{ padding:'0 16px 16px', borderTop:'1px solid #f3f4f6' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:14, marginTop:10 }}>
                  <thead>
                    <tr>
                      <th style={{...s.th,width:32}}></th>
                      {['Order No','Product','Qty','Unit Price','Total','Remarks','Date'].map(h=>(
                        <th key={h} style={h==='Qty'||h==='Unit Price'||h==='Total'?{...s.th,textAlign:'center'}:s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {customer.orders.map(o=>{
                      const isDesel = (deselectedOrders[cid]||new Set()).has(o.id);
                      return (
                      <tr key={o.id} style={{ borderBottom:'1px solid #f3f4f6', opacity: isDesel ? 0.4 : 1 }}>
                        <td style={{ ...s.td, width:32 }}><input type="checkbox" checked={!isDesel} onChange={()=>toggleOrder(cid,o.id)} style={{ cursor:'pointer', accentColor:'#7c6af5' }} /></td>
                        <td style={{ ...s.td, fontFamily:'monospace', color:'#7c6af5', fontWeight:700, fontSize:11 }}>{o.order_number}</td>
                        <td style={s.td}>{o.product_code||'—'}</td>
                        <td style={{ ...s.td, textAlign:'center', fontWeight:700 }}>{o.quantity}</td>
                        <td style={{ ...s.td, textAlign:'right', fontWeight:600 }}>${o.unit_price.toFixed(2)}</td>
                        <td style={{ ...s.td, textAlign:'right', fontWeight:700, color:'#16a34a' }}>${o.total.toFixed(2)}</td>
                        <td style={{ ...s.td, color:'#374151', maxWidth:180 }}>{o.remarks||'—'}</td>
                        <td style={{ ...s.td, color:'#6b7280', whiteSpace:'nowrap', fontSize:11 }}>{o.created_at}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ display:'flex', gap:10, marginBottom:10, flexWrap:'wrap', alignItems:'flex-end' }}>
                  <div style={{ flex:'0 0 130px' }}>
                    <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:3 }}>
                      Delivery Fee ($) {freeDelivery&&<span style={{color:'#16a34a'}}>— Free!</span>}
                    </label>
                    <input style={{ ...s.inp, width:'100%' }} type="number" step="0.5" value={form.delivery_fee}
                      onChange={e=>updateForm(cid,'delivery_fee',e.target.value)} />
                  </div>
                  <div style={{ flex:'0 0 130px' }}>
                    <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:3 }}>Discount ($)</label>
                    <input style={{ ...s.inp, width:'100%' }} type="number" step="0.5" value={form.discount}
                      onChange={e=>updateForm(cid,'discount',e.target.value)} />
                  </div>
                  <div style={{ flex:'1 1 200px' }}>
                    <label style={{ fontSize:11, fontWeight:600, color:'#6b7280', display:'block', marginBottom:3 }}>Staff Note</label>
                    <input style={{ ...s.inp, width:'100%' }} value={form.staff_note||''} placeholder="Optional..."
                      onChange={e=>updateForm(cid,'staff_note',e.target.value)} />
                  </div>
                </div>

                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f9fafb', borderRadius:8, padding:'10px 14px', flexWrap:'wrap', gap:10 }}>
                  <div style={{ display:'flex', gap:20, fontSize:13, flexWrap:'wrap' }}>
                    <span style={{color:'#374151'}}>Subtotal: <strong>${subtotal.toFixed(2)}</strong></span>
                    {parseFloat(form.delivery_fee)>0&&<span style={{color:'#374151'}}>Delivery: <strong>+${parseFloat(form.delivery_fee).toFixed(2)}</strong></span>}
                    {parseFloat(form.discount)>0&&<span style={{color:'#dc2626'}}>Discount: <strong>-${parseFloat(form.discount).toFixed(2)}</strong></span>}
                    <span style={{color:'#111827',fontWeight:700,fontSize:15}}>Total: ${grandTotal.toFixed(2)}</span>
                  </div>
                  <button onClick={()=>createBill(customer)} disabled={creating[cid]}
                    style={{ background:'linear-gradient(135deg,#7c6af5,#c084fc)', color:'#fff', border:'none', borderRadius:8, padding:'8px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                    {creating[cid]?'Creating...':'✅ Create Bill'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {postponeModal&&<PostponeModal customer={postponeModal} onClose={()=>setPostponeModal(null)} onSaved={()=>{setPostponeModal(null);loadQueue();}} />}
    </div>
  );
}
